import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import {
	AutoCoverOptions, AUTO_COVER_FABRIC_COLORS, AUTO_COVER_MANUFACTURER_OTHER,
	AUTO_COVER_SLIDER_OPTIONS, IAutoCoverColorSwatch, IAutoCoverPictureOption,
	IAutoCoverSpecModel, IDropdownModel, calculateLathamSlack, splitAutoCoverInches
} from "@models";
import { autoCoverReplacementBrand } from "@services/auto-cover-to-latham-order.service";

export type AutoCoverSection = "all" | "mechanism" | "fabric" | "electronics" | "boxLid";

type AutoCoverPictureField = "sliders" | "guideFeedType";

// Box & Lid sizes, stored as inches and entered as feet and inches
type AutoCoverBoxMeasure = "housingWidth" | "housingLength" | "housingDepth" | "motorSideOffset" | "nonMotorSideOffset";

const BOX_MEASURES: AutoCoverBoxMeasure[] = ["housingWidth", "housingLength", "housingDepth", "motorSideOffset", "nonMotorSideOffset"];

// The Auto Cover spec sheet form. One component drives both surfaces: the desktop job tab renders
// every section at once ([section]="'all'"), the mobile wizard renders one section per step. Keeping
// it in one place means the two can't drift apart on fields or option lists.
@Component({
	selector: "auto-cover-specs",
	templateUrl: "./auto-cover-specs.component.html",
	styleUrls: ["./auto-cover-specs.component.scss"]
})
export class AutoCoverSpecsComponent implements OnChanges {
	@Input() spec: IAutoCoverSpecModel;
	@Input() section: AutoCoverSection = "all";
	@Input() isMobile: boolean = false;
	@Input() readOnly: boolean = false;

	readonly manufacturerOther: string = AUTO_COVER_MANUFACTURER_OTHER;

	// Manufacturer-independent lists
	manufacturers: IDropdownModel[] = AutoCoverOptions.manufacturers;
	unitLocations: IDropdownModel[] = AutoCoverOptions.unitLocations;
	poolTypes: IDropdownModel[] = AutoCoverOptions.poolTypes;
	motorTypes: IDropdownModel[] = AutoCoverOptions.motorTypes;
	switchTypes: IDropdownModel[] = AutoCoverOptions.switchTypes;
	webbingTypes: IDropdownModel[] = AutoCoverOptions.webbingTypes;
	trackTypes: IDropdownModel[] = AutoCoverOptions.trackTypes;
	leAttachments: IDropdownModel[] = AutoCoverOptions.leAttachments;
	leSizes: IDropdownModel[] = AutoCoverOptions.leSizes;
	housingTypes: IDropdownModel[] = AutoCoverOptions.housingTypes;
	lidTypes: IDropdownModel[] = AutoCoverOptions.lidTypes;

	// Narrowed by the selected manufacturer (and, for guide feeds, the track type)
	models: IDropdownModel[] = [];
	ropeAttachments: IDropdownModel[] = [];
	guideFeedOptions: IAutoCoverPictureOption[] = [];

	fabricColors: IAutoCoverColorSwatch[] = AUTO_COVER_FABRIC_COLORS;
	sliderOptions: IAutoCoverPictureOption[] = AUTO_COVER_SLIDER_OPTIONS;

	// The picture picker that's open, if any. Sliders and Guide Feeds share it.
	pictureField: AutoCoverPictureField = null;

	// Pictures that haven't been supplied yet (see AUTO_COVER_SLIDER_OPTIONS). Their tiles fall back to an
	// icon, and remembering the miss keeps every change detection from asking for the file again.
	missingPictures = new Set<string>();

	// The feet and inches boxes for each Box & Lid size. Held here while the tech types rather than
	// re-derived from the stored inches on every change detection, which would turn "30" in the inches
	// box into 2 ft 6 in mid keystroke.
	private boxParts: Record<string, { feet: number, inches: number }> = {};

	ngOnChanges(changes: SimpleChanges) {
		if (changes["spec"]) {
			this.refreshManufacturerOptions();
			this.refreshBoxParts();
		}
	}

	showSection(section: AutoCoverSection): boolean {
		return this.section === "all" || this.section === section;
	}

	// "Other" has no model list on the spec sheet, so the brand and model both become free text.
	get isOtherManufacturer(): boolean {
		return this.spec?.manufacturer === AUTO_COVER_MANUFACTURER_OTHER;
	}

	onManufacturerChanged(manufacturer: string) {
		if (!this.spec || manufacturer === this.spec.manufacturer)
			return;

		this.spec.manufacturer = manufacturer;

		// The narrowed lists no longer apply, so clear what they were feeding rather than leaving a
		// value behind that the new manufacturer doesn't offer.
		this.spec.model = null;
		this.spec.ropeAttachment = null;

		if (!this.isOtherManufacturer)
			this.spec.manufacturerOther = null;

		this.refreshManufacturerOptions();
		this.clearGuideFeedIfNotOffered();
	}

	onTrackTypeChanged(trackType: string) {
		if (!this.spec)
			return;

		this.spec.trackType = trackType;

		this.refreshGuideFeedOptions();
		this.clearGuideFeedIfNotOffered();
	}

	onFabricColorClicked(color: string) {
		if (this.readOnly || !this.spec)
			return;

		this.spec.fabricColor = this.spec.fabricColor === color ? null : color;
	}

	// ---- slack ----

	// Latham's standard slack for this width and track type, worked out exactly as the replacement
	// quote's Slack field will work it out (same brand, same rules). Null until both are recorded.
	get calculatedSlack(): number {
		if (!this.spec?.trackType || !this.spec.widthFeet)
			return null;

		return calculateLathamSlack(autoCoverReplacementBrand(), this.spec.trackType === "Undertrack", this.spec.widthFeet);
	}

	// What the box shows: the tech's override, otherwise the standard value
	get displayedSlack(): number {
		return this.spec?.slack ?? this.calculatedSlack;
	}

	get isSlackOverridden(): boolean {
		return this.spec?.slack !== null && this.spec?.slack !== undefined;
	}

	// Applied on change rather than per keystroke, so clearing the box to type a new number doesn't
	// snap the standard value back in under the cursor. Clearing it, or typing the standard value, goes
	// back to automatic and follows the width from then on.
	onSlackChanged(value: string) {
		if (!this.spec)
			return;

		const slack = (value === null || value === undefined || value === "") ? null : Math.round(Number(value));

		this.spec.slack = (slack === null || isNaN(slack) || slack === this.calculatedSlack) ? null : slack;
	}

	resetSlack() {
		if (this.spec && !this.readOnly)
			this.spec.slack = null;
	}

	// ---- Box & Lid sizes in feet and inches ----

	boxFeet(field: AutoCoverBoxMeasure): number {
		return this.boxParts[field]?.feet;
	}

	boxInches(field: AutoCoverBoxMeasure): number {
		return this.boxParts[field]?.inches;
	}

	onBoxFeetChanged(field: AutoCoverBoxMeasure, value: any) {
		this.setBoxPart(field, "feet", value);
	}

	onBoxInchesChanged(field: AutoCoverBoxMeasure, value: any) {
		this.setBoxPart(field, "inches", value);
	}

	private setBoxPart(field: AutoCoverBoxMeasure, part: "feet" | "inches", value: any) {
		if (!this.spec)
			return;

		const parts = this.boxParts[field] ?? (this.boxParts[field] = { feet: null, inches: null });
		parts[part] = (value === null || value === undefined || value === "" || isNaN(Number(value))) ? null : Number(value);

		// Both boxes empty means nothing recorded, not zero
		this.spec[field] = (parts.feet === null && parts.inches === null)
			? null
			: (parts.feet ?? 0) * 12 + (parts.inches ?? 0);
	}

	private refreshBoxParts() {
		this.boxParts = {};
		BOX_MEASURES.forEach(field => this.boxParts[field] = splitAutoCoverInches(this.spec?.[field]));
	}

	// ---- picture pickers: Sliders and Guide Feeds ----

	get selectedSlider(): IAutoCoverPictureOption {
		return this.pictureFor(this.sliderOptions, this.spec?.sliders);
	}

	get selectedGuideFeed(): IAutoCoverPictureOption {
		return this.pictureFor(this.guideFeedOptions, this.spec?.guideFeedType);
	}

	get pictureOptions(): IAutoCoverPictureOption[] {
		return this.pictureField === "guideFeedType" ? this.guideFeedOptions : this.sliderOptions;
	}

	get pictureTitle(): string {
		return this.pictureField === "guideFeedType" ? "Guide Feeds" : "Sliders";
	}

	get pictureValue(): string {
		return this.pictureField ? this.spec?.[this.pictureField] : null;
	}

	hasPicture(option: IAutoCoverPictureOption): boolean {
		return !!option?.imageUrl && !this.missingPictures.has(option.imageUrl);
	}

	onPictureMissing(option: IAutoCoverPictureOption) {
		if (option?.imageUrl)
			this.missingPictures.add(option.imageUrl);
	}

	openPicturePicker(field: AutoCoverPictureField) {
		if (this.readOnly)
			return;

		// Same as auto-cover-select: drop the keyboard a text box left up on iOS
		(document.activeElement as HTMLElement)?.blur?.();
		this.pictureField = field;
	}

	closePicturePicker() {
		this.pictureField = null;
	}

	onPicturePicked(option: IAutoCoverPictureOption) {
		if (this.readOnly || !this.spec || !this.pictureField)
			return;

		this.spec[this.pictureField] = option.id;
		this.closePicturePicker();
	}

	// A value recorded before the lists changed (the old guide feed names) isn't in the list any more,
	// but still shows as recorded rather than looking empty
	private pictureFor(options: IAutoCoverPictureOption[], value: string): IAutoCoverPictureOption {
		if (!value)
			return null;

		return options.find(x => x.id === value) ?? { id: value, text: value };
	}

	private clearGuideFeedIfNotOffered() {
		if (this.spec?.guideFeedType && !this.guideFeedOptions.some(x => x.id === this.spec.guideFeedType))
			this.spec.guideFeedType = null;
	}

	private refreshGuideFeedOptions() {
		this.guideFeedOptions = AutoCoverOptions.guideFeedsFor(this.spec?.manufacturer, this.spec?.trackType);
	}

	private refreshManufacturerOptions() {
		const manufacturer = this.spec?.manufacturer;

		this.models = AutoCoverOptions.modelsForManufacturer(manufacturer);
		this.ropeAttachments = AutoCoverOptions.ropeAttachmentsForManufacturer(manufacturer);
		this.refreshGuideFeedOptions();
	}
}
